import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { useStores } from '../../../cls/stores/MobXProvider'
import BudgetLst from './BudgetLst'
import { PageLoader } from '../../../components/Shared'
import {
	actLoadBudget,
	requireBudgetById
} from '../../../stores/budget/actions'
import BudgetEdit from './BudgetEdit'
import { useSmartTranslation } from '../../../cls/TranslationContext'
/**
 * @component BudgetMain
 * @description
 * Main entry point for the Budget Management module in the application.
 * Handles route-level rendering of the budget list and individual budget views or editors.
 *
 * It ensures that only authenticated users with admin privileges (`isAdmin` or `isSuperAdmin`) can access the routes.
 * If a user lacks permissions, it redirects them to the logout page or displays an unauthorized message.
 *
 * The component uses `react-router-dom`'s `<Routes>` and `<Route>` to render:
 * - The budget list at `/budget`
 * - Budget editor at `/budget/:itmId/edit`
 * - Budget viewer at `/budget/:itmId/view`
 *
 * It also wraps detailed views in a `PageLoader` to handle asynchronous loading of budget data.
 *
 * @returns {React.ReactElement} The routed layout for the budget management module.
 *
 * @access Admin-only
 *
 * @example
 * // In your router definition
 * <Route path="budget/*" element={<BudgetMain />} />
 */
const BudgetMain = () => {
	const navigate = useNavigate()
	const { users } = useStores()
	const { loggedInUserId, loggedInUser } = users
	const isAdmin = loggedInUser?.isAdmin
	const isSuperAdmin = loggedInUser?.isSuperAdmin
	const { t } = useSmartTranslation()

	useEffect(() => {
		if (!users.isLoggedIn) {
			navigate('/logout')
			return
		}
	}, [loggedInUserId])

	// If the user does not have personal permission
	// and lacks the required permissions for the action and resource
	// Display a message indicating lack of permissions
	if (!(isAdmin || isSuperAdmin)) {
		return (
			<div style={{ textAlign: 'center' }}>
				{t('__denied.access').charAt(0).toUpperCase() +
					t('__denied.access%__feature-off%_budget')
						.toLowerCase()
						.slice(1)}
			</div>
		)
	}

	return (
		<Routes>
			<Route index element={<BudgetLst />} />
			<Route
				path=":itmId/*"
				element={
					<PageLoader
						forceLoad={true}
						requireItm={(id) => {
							return requireBudgetById(id)
						}}
						loadItm={(id) => actLoadBudget(id)}
						targetLst="budget"
					/>
				}
			>
				<Route path="edit" element={<BudgetEdit />} />
				<Route path="view" element={<BudgetEdit viewOnly={true} />} />
			</Route>
			<Route path="*" element={<div>Not found</div>} />
		</Routes>
	)
}
export default observer(BudgetMain)
